import { Coupon } from "./types";

/**
 * What a coupon leaves to pay on a set of line totals.
 *
 * **The same rule the server applies**, in
 * `domain/services/subscription/pricing.py`: the percentage comes off **each
 * line** and rounds half up, because that is how the provider bills it and a
 * total discounted in one go can differ by a cent from the invoice.
 *
 * It exists twice on purpose, and only here. A centre's summary is already
 * arithmetic the browser does — it multiplies a unit price by a seat count
 * the server has not been told about yet — so the discount has to be applied
 * in the same place or the figure beside it would be the undiscounted one.
 * The therapist's screen asks the server instead, which is why this is not
 * used there. `coupon.test.ts` pins the same examples the Python tests pin.
 *
 * Null means it cannot be stated: no coupon, one tied to particular prices,
 * or one whose minimum this purchase has not reached. The screen then says
 * the discount lands at payment rather than inventing a number.
 */
export const payableWithCoupon = (
    lineTotals: number[],
    coupon: Coupon | null,
    minimumCents?: number | null
): number | null => {
    if (!coupon) return null;

    const total = lineTotals.reduce((sum, line) => sum + line, 0);
    if (total <= 0) return null;
    if (minimumCents != null && total < minimumCents) return null;

    if (coupon.percentOff != null) {
        return lineTotals.reduce((kept, line) => {
            // Half up on the discount, so nobody is charged a cent more than
            // the percentage says.
            const off = Math.floor((line * coupon.percentOff! + 50) / 100);
            return kept + line - off;
        }, 0);
    }
    if (coupon.amountOffCents != null) {
        return Math.max(total - coupon.amountOffCents, 0);
    }
    return null;
};

/**
 * What to say when a checkout could not start.
 *
 * One case has a reason the reader can act on: the provider refused a live
 * code for this account — one limited to a first purchase, say, on an
 * account that has paid before. Everything else is the generic
 * message, because nothing else tells the reader what to do differently.
 */
export const checkoutErrorKey = (error: unknown): string => {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail;
    if (detail === "coupon_not_redeemable") return "subscription.coupon.notRedeemable";
    // Moving up to Max charges the difference now; the card declined it and
    // nothing changed.
    if (detail === "payment_declined") return "subscription.billing.tier.declined";
    return "subscription.billing.actionError";
};
